/**
 * HoneyDo API Client
 * Based on Tim-Combo's proven API patterns
 * Handles all API communication with proper error handling and response formatting
 */

// API response types that match the standard response format
export const isSuccessResponse = (response: any): boolean => {
  return response?.success === true;
};

export const isErrorResponse = (response: any): boolean => {
  return response?.success === false;
};

// Base API URL from environment or default (pilot API)
const API_BASE = process.env.REACT_APP_API_URL || 'https://lyr98uple4.execute-api.us-east-2.amazonaws.com/dev';

// API endpoints for HoneyDo Platform (relative paths - API_BASE is added in Make_Authorized_API_Call)
export const API_ENDPOINTS = {
  // Health Check
  HEALTH: '/health',

  // Authentication endpoints
  AUTH: {
    REGISTER: '/auth/register',
    LOGIN: '/auth/login',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
    VERIFY: '/auth/verify'
  },

  // User Management
  USER: {
    PROFILE: '/users/profile',
    UPDATE_PROFILE: '/users/profile',
    PREFERENCES: '/users/preferences',
    SETTINGS: '/users/settings'
  },

  // Entity Management (Families, Organizations)
  ENTITIES: {
    LIST: '/entities',
    CREATE: '/entities',
    GET: (entityId: string) => `/entities/${entityId}`,
    UPDATE: (entityId: string) => `/entities/${entityId}`,
    DELETE: (entityId: string) => `/entities/${entityId}`,
    MEMBERS: (entityId: string) => `/entities/${entityId}/members`,
    RELATIONSHIPS: (entityId: string) => `/entities/${entityId}/relationships`
  },

  // Family Management
  FAMILY: {
    GET: (familyId: string) => `/family/${familyId}`,
    CREATE: '/family',
    UPDATE: (familyId: string) => `/family/${familyId}`,
    INVITE: '/family/invite',
    USER_INVITE: '/family/user-invite', // Cognito AdminCreateUser email invite
    JOIN: '/family/join',
    ACCEPT_INVITE: '/family/accept-invite',
    MEMBERS: (familyId: string) => `/family/${familyId}/members`,
    REMOVE_MEMBER: (familyId: string, memberId: string) => `/family/${familyId}/members/${memberId}`,
    UPDATE_MEMBER_ROLE: (familyId: string, memberId: string) => `/family/${familyId}/members/${memberId}/role`,
    STATS: (familyId: string) => `/family/${familyId}/stats`
  },

  // Task Management
  TASKS: {
    LIST: '/tasks',
    CREATE: '/tasks',
    GET: (taskId: string) => `/tasks/${taskId}`,
    UPDATE: (taskId: string) => `/tasks/${taskId}`,
    DELETE: (taskId: string) => `/tasks/${taskId}`,
    COMPLETE: (taskId: string) => `/tasks/${taskId}/complete`,
    ASSIGN: (taskId: string) => `/tasks/${taskId}/assign`,
    COMMENT: (taskId: string) => `/tasks/${taskId}/comments`,
    OUTSOURCE: (taskId: string) => `/tasks/${taskId}/outsource`,
    HISTORY: (taskId: string) => `/tasks/${taskId}/history`,
    ATTACHMENTS: (taskId: string) => `/tasks/${taskId}/attachments`,
    AGING_PRIORITY: '/tasks/aging-priority',
    BULK_UPDATE: '/tasks/bulk-update'
  },

  // Event Management (hierarchical tasks)
  EVENTS: {
    LIST: '/events',
    CREATE: '/events',
    GET: (eventId: string) => `/events/${eventId}`,
    UPDATE: (eventId: string) => `/events/${eventId}`,
    DELETE: (eventId: string) => `/events/${eventId}`,
    PROGRESS: (eventId: string) => `/events/${eventId}/progress`,
    TASKS: (eventId: string) => `/events/${eventId}/tasks`,
    ADD_TASK: (eventId: string) => `/events/${eventId}/tasks`
  },

  // Contractor & Outsourcing
  CONTRACTORS: {
    SEARCH: '/contractors/search',
    GET: (contractorId: string) => `/contractors/${contractorId}`,
    RECOMMEND: (taskId: string) => `/tasks/${taskId}/contractor-recommendations`,
    REQUEST_QUOTE: '/contractors/request-quote',
    REVIEWS: (contractorId: string) => `/contractors/${contractorId}/reviews`,
    CATEGORIES: '/contractors/categories'
  },
  
  // Agent Factory Configuration
  AGENTS: {
    TRIGGERS: {
      LIST: '/config/agent-triggers',
      CREATE: '/config/agent-triggers',
      GET: (triggerId: string) => `/config/agent-triggers/${triggerId}`,
      UPDATE: (triggerId: string) => `/config/agent-triggers/${triggerId}`,
      DELETE: (triggerId: string) => `/config/agent-triggers/${triggerId}`,
      TEST: (triggerId: string) => `/config/agent-triggers/${triggerId}/test`,
      EXECUTE: (triggerId: string) => `/config/agent-triggers/${triggerId}/execute`
    },
    TYPES: '/config/agent-types',
    HISTORY: '/config/agent-history',
    METRICS: '/config/agent-metrics'
  },
  
  // Notifications
  NOTIFICATIONS: {
    LIST: '/notifications',
    MARK_READ: (notificationId: string) => `/notifications/${notificationId}/read`,
    PREFERENCES: '/notifications/preferences',
    SUBSCRIBE: '/notifications/subscribe',
    UNSUBSCRIBE: '/notifications/unsubscribe'
  },
  
  // Analytics & Insights
  ANALYTICS: {
    DASHBOARD: '/analytics/dashboard',
    TASK_METRICS: '/analytics/task-metrics',
    FAMILY_ACTIVITY: '/analytics/family-activity',
    CONTRACTOR_PERFORMANCE: '/analytics/contractor-performance',
    AGING_ANALYSIS: '/analytics/aging-analysis',
    PREDICTIVE: {
      TASK_COMPLETION: '/analytics/predictive/task-completion',
      OUTSOURCE_LIKELIHOOD: '/analytics/predictive/outsource-likelihood',
      MEMBER_ENGAGEMENT: '/analytics/predictive/member-engagement'
    }
  },
  
  // File Upload
  FILES: {
    UPLOAD: '/files/upload',
    GET_UPLOAD_URL: '/files/get-upload-url',
    DELETE: (fileId: string) => `/files/${fileId}`
  },
  
  // Search
  SEARCH: {
    TASKS: '/search/tasks',
    CONTRACTORS: '/search/contractors',
    GLOBAL: '/search'
  },
  
  // Premium Features
  PREMIUM: {
    SUBSCRIPTION: '/subscription',
    PLANS: '/premium/plans',
    BILLING: '/premium/billing',
    FEATURES: '/premium/features'
  },
  
  // Calendar Integration
  CALENDAR: {
    AUTH: {
      CONNECT: '/calendar/auth/connect',
      CALLBACK: '/calendar/auth/callback',
      DISCONNECT: (accountId: string) => `/calendar/auth/disconnect/${accountId}`,
      REFRESH: '/calendar/auth/refresh'
    },
    ACCOUNTS: {
      LIST: '/calendar/accounts',
      GET: (accountId: string) => `/calendar/accounts/${accountId}`,
      UPDATE: (accountId: string) => `/calendar/accounts/${accountId}`,
      SYNC: (accountId: string) => `/calendar/sync/${accountId}`
    },
    EVENTS: {
      LIST: '/calendar/events',
      CREATE: '/calendar/events',
      GET: (eventId: string) => `/calendar/events/${eventId}`,
      UPDATE: (eventId: string) => `/calendar/events/${eventId}`,
      DELETE: (eventId: string) => `/calendar/events/${eventId}`,
      SYNC: '/calendar/events/sync',
      CONFLICTS: '/calendar/events/conflicts'
    },
    SCHEDULING: {
      OPTIMAL_SLOT: '/calendar/scheduling/optimal-slot',
      CONFLICTS: '/calendar/scheduling/conflicts',
      WEATHER_ADJUST: '/calendar/scheduling/weather-adjust'
    }
  },
  
  // Weather Integration (deployed at /scheduling/weather)
  WEATHER: {
    FORECAST: '/scheduling/weather',
    CURRENT: '/scheduling/weather',
    ALERTS: '/scheduling/weather'
  }
};

/**
 * Make an authorized API call with proper error handling
 * This is the EXACT Tim-Combo pattern - Make_Authorized_API_Call
 *
 * @param {string} endpoint - API endpoint to call
 * @param {string} method - HTTP method (GET, POST, PUT, DELETE)
 * @param {any} body - Request body for POST/PUT
 * @param {object} options - Additional options (params, headers)
 * @returns {Promise<APIResponse>} Standard API response format
 */
export const Make_Authorized_API_Call = async (
  endpoint: string,
  method: string = 'GET',
  body: any = undefined,
  options: any = {}
): Promise<any> => {
  try {
    // Get the auth token from localStorage (Tim-Combo pattern)
    const token = localStorage.getItem('token');

    // Log the request details for debugging
    console.log(`[API] Making ${method} request to: ${endpoint}`, {
      options,
      hasToken: !!token,
      hasBody: !!body
    });

    // Build the fetch options
    const fetchOptions: any = {
      method,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token ? `Bearer ${token}` : '',
        ...(options.headers || {})
      }
    };

    // Add the request body for POST/PUT requests
    if (['POST', 'PUT'].includes(method) && body !== undefined) {
      fetchOptions.body = JSON.stringify(body);
    }

    // Build the URL with query params if provided
    // Ensure we're using the API_BASE for the URL
    let url = endpoint.startsWith('http') ? endpoint : API_BASE + endpoint;

    if (options.params) {
      const searchParams = new URLSearchParams();
      Object.entries(options.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      const queryString = searchParams.toString();
      if (queryString) {
        url = `${url}${url.includes('?') ? '&' : '?'}${queryString}`;
      }
    }

    // Make the fetch request
    const response = await fetch(url, fetchOptions);

    // Check response status and content type
    const contentType = response.headers.get('content-type');
    const isJson = contentType && contentType.includes('application/json');

    // Process JSON responses
    if (isJson) {
      try {
        const data = await response.json();
        return data;
      } catch (jsonError) {
        console.error('[API] JSON parse error:', jsonError);

        return {
          success: false,
          message: 'Failed to parse API response as JSON',
          meta: {
            Timestamp: new Date().toISOString(),
            Error_Source: 'client',
          },
          error_code: 'json_parse_error',
          error_details: `Status ${response.status} ${response.statusText} for ${url}`
        };
      }
    }

    // Handle non-JSON responses
    try {
      const textContent = await response.text();
      const preview = textContent.substring(0, 150) + (textContent.length > 150 ? '...' : '');
      console.error(`[API] Non-JSON response: ${contentType} for ${url}, preview: ${preview}`);

      return {
        success: false,
        message: 'Server returned non-JSON response',
        meta: {
          Timestamp: new Date().toISOString(),
          Error_Source: 'server',
        },
        error_code: 'non_json_response',
        error_details: `Content-Type: ${contentType || 'unknown'}, Status: ${response.status}`
      };
    } catch (e: any) {
      console.error('[API] Could not read response text:', e);

      return {
        success: false,
        message: 'Failed to read server response',
        meta: {
          Timestamp: new Date().toISOString(),
          Error_Source: 'client',
        },
        error_code: 'response_read_error',
        error_details: e.toString()
      };
    }
  } catch (error: any) {
    // Handle network errors and exceptions
    console.error('[API] Network error:', error);

    return {
      success: false,
      message: error.message || 'Network request failed',
      meta: {
        Timestamp: new Date().toISOString(),
        Error_Source: 'network',
      },
      error_code: 'network_error',
      error_details: error.toString()
    };
  }
};

/**
 * Legacy makeApiCall function - kept for backward compatibility
 * New code should use Make_Authorized_API_Call directly
 */
export const makeApiCall = Make_Authorized_API_Call;

/**
 * Convenience methods for common HTTP verbs
 */
export const apiClient = {
  get: (endpoint: string, options?: any) => makeApiCall(endpoint, 'GET', null, options),
  post: (endpoint: string, body?: any, options?: any) => makeApiCall(endpoint, 'POST', body, options),
  put: (endpoint: string, body?: any, options?: any) => makeApiCall(endpoint, 'PUT', body, options),
  patch: (endpoint: string, body?: any, options?: any) => makeApiCall(endpoint, 'PATCH', body, options),
  delete: (endpoint: string, options?: any) => makeApiCall(endpoint, 'DELETE', null, options)
};

/**
 * Helper function to handle API responses consistently
 */
export const handleApiResponse = async (promise: Promise<any>, options: any = {}): Promise<any> => {
  const { onSuccess, onError, showNotification = true } = options;
  
  try {
    const response = await promise;
    
    if (isSuccessResponse(response)) {
      if (onSuccess) {
        onSuccess(response);
      }
      if (showNotification && response.message) {
        // You can integrate with your notification system here
        console.log('[Success]', response.message);
      }
      return response;
    } else {
      const error: any = new Error(response.message || 'Request failed');
      error.response = response;
      throw error;
    }
  } catch (error: any) {
    if (onError) {
      onError(error);
    }
    if (showNotification) {
      // You can integrate with your notification system here
      console.error('[Error]', error.message);
    }
    throw error;
  }
};

/**
 * Backward compatibility aliases for legacy imports
 */
export const Make_HoneyDo_API_Call = Make_Authorized_API_Call;
export const honeyDoApiClient = apiClient;

/**
 * Export a default client instance
 */
export default {
  ...apiClient,
  Make_Authorized_API_Call,
  Make_HoneyDo_API_Call,
  honeyDoApiClient,
  endpoints: API_ENDPOINTS,
  isSuccessResponse,
  isErrorResponse,
  handleApiResponse
};